import { chipClasses } from '@mui/material/Chip';
import type { Components, Theme } from '@mui/material/styles';
import { toggleButtonClasses } from '@mui/material/ToggleButton';

import type { CheckState, TintScale } from './looks';
import { SHAPE, TEXT_SIZE, tinted, WEIGHT } from './scales';

const BADGE_TINTS: [color: 'success' | 'warning' | 'info' | 'error', scale: TintScale][] = [
  ['success', 'success'],
  ['warning', 'warning'],
  ['info', 'brand'],
  ['error', 'danger'],
];

const CHECK_TINTS: [state: CheckState, scale: TintScale][] = [
  ['pass', 'accent'],
  ['warn', 'warning'],
  ['fail', 'danger'],
];

/** The kit choices: chips and the tonal badge chip, segmented buttons, check marks and calendar days. */
export const CHOICES: Components<Theme> = {
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: SHAPE.small },
      sizeMedium: { fontSize: TEXT_SIZE.control },
      sizeSmall: ({ theme }) => ({ fontSize: theme.typography.caption.fontSize }),
      filled: ({ theme }) => ({
        [`&.${chipClasses.colorDefault}`]: {
          backgroundColor: theme.vars.palette.action.hover,
          color: theme.vars.palette.grey[700],
        },
      }),
      outlined: ({ theme }) => ({
        [`&.${chipClasses.colorDefault}`]: {
          borderColor: theme.vars.palette.grey[300],
          color: theme.vars.palette.grey[700],
          [`&.${chipClasses.clickable}:hover`]: {
            borderColor: theme.vars.palette.primary.light,
            backgroundColor: theme.vars.palette.tint.brand.fill,
          },
        },
      }),
    },
    variants: [
      {
        props: { variant: 'tonal' },
        style: ({ theme }) => ({
          ...theme.typography.overline,
          height: 'auto',
          padding: '2px 10px',
          fontSize: theme.typography.tag.fontSize,
          borderRadius: SHAPE.full,
          color: theme.vars.palette.grey[700],
          backgroundColor: theme.vars.palette.action.hover,
          [`& .${chipClasses.label}`]: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0 },
        }),
      },
      ...BADGE_TINTS.map(([color, scale]) => ({
        props: { variant: 'tonal' as const, color },
        style: ({ theme }: { theme: Theme }) => tinted(theme, scale),
      })),
    ],
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        gap: 2,
        padding: 2,
        borderRadius: SHAPE.full,
        backgroundColor: theme.vars.palette.grey[100],
      }),
      grouped: { border: 0, borderRadius: SHAPE.full, paddingInline: 12 },
    },
  },
  MuiToggleButton: {
    defaultProps: { disableFocusRipple: true },
    styleOverrides: {
      root: ({ theme }) => ({
        gap: 6,
        textTransform: 'none',
        [`&.${toggleButtonClasses.sizeSmall}`]: { ...theme.typography.body2, paddingBlock: 4 },
        [`&.${toggleButtonClasses.sizeMedium}`]: { ...theme.typography.body1, paddingBlock: 6 },
        color: theme.vars.palette.text.secondary,
        '&:hover': { color: theme.vars.palette.text.primary, backgroundColor: 'transparent' },
        [`&.${toggleButtonClasses.selected}, &.${toggleButtonClasses.selected}:hover`]: {
          color: theme.vars.palette.text.heading,
          backgroundColor: theme.vars.palette.background.paper,
          boxShadow: theme.shadows[1],
          ...theme.applyStyles('dark', { backgroundColor: theme.vars.palette.grey[300] }),
        },
      }),
    },
  },
  KitCheckMark: {
    styleOverrides: {
      root: {
        display: 'flex',
        width: 24,
        height: 24,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
        borderRadius: SHAPE.full,
      },
    },
    variants: CHECK_TINTS.map(([state, scale]) => ({
      props: { state },
      style: ({ theme }: { theme: Theme }) => tinted(theme, scale),
    })),
  },
  KitCalendarDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.caption,
        height: 28,
        borderRadius: SHAPE.compact,
        fontVariantNumeric: 'tabular-nums',
        color: theme.vars.palette.grey[700],
        '&:hover': { backgroundColor: theme.vars.palette.action.hover },
      }),
    },
    variants: [
      { props: { outside: true }, style: ({ theme }) => ({ color: theme.vars.palette.grey[300] }) },
      {
        props: { between: true },
        style: ({ theme }) => ({ ...tinted(theme, 'brand'), '&:hover': tinted(theme, 'brand') }),
      },
      {
        props: { edge: true },
        style: ({ theme }) => ({
          fontWeight: WEIGHT.bold,
          color: theme.vars.palette.primary.contrastText,
          backgroundColor: theme.vars.palette.primary.main,
          '&:hover': { backgroundColor: theme.vars.palette.primary.main },
        }),
      },
    ],
  },
};
